/**
 * `<caos-layout>` — puts the pieces inside a section side by side, in columns that respond to the
 * width they have. (Catalog: Layout & containers › Layout — puts the pieces inside a section side by side, in columns that respond to the width they have.)
 *
 * CAOS-590. A page template's REGIONS decide where a section sits on a page; a layout decides how the
 * pieces sit INSIDE a section. It is placed in a section like any other piece and holds
 * `<caos-layout-item>`s. It never holds a section and never stands in for a region, so an author has
 * one answer to "where does this section go" and a different one to "how is this section arranged".
 *
 * The layout is one wrapping row. Each item says how much of the row it takes, and how that changes at
 * each named breakpoint; the layout says only how the items share what is left over along the row
 * (`horizontal-align`) and where they sit across it (`vertical-align`). Items that do not fit wrap onto
 * the next line, which is what turns two half-width columns into a stack when each asks for the full
 * width on a narrow screen.
 *
 * It adds no stacking layer and no overflow rule of its own. A piece inside it that opens a panel
 * still opens it through the platform, and nothing inside it is clipped by it.
 *
 * Styling seam: `::part(layout)`.
 */
import { CaosElement, define, makeSheet } from '../base-element.js';

/** How items share the space left over along the row. */
export const LAYOUT_HORIZONTAL_ALIGN = ['start', 'center', 'end', 'space', 'spread'] as const;
/** Where items of different heights sit across the row. */
export const LAYOUT_VERTICAL_ALIGN = ['start', 'center', 'end', 'stretch'] as const;

const JUSTIFY: Record<(typeof LAYOUT_HORIZONTAL_ALIGN)[number], string> = {
  start: 'flex-start',
  center: 'center',
  end: 'flex-end',
  space: 'space-around',
  spread: 'space-between',
};
const ALIGN: Record<(typeof LAYOUT_VERTICAL_ALIGN)[number], string> = {
  start: 'flex-start',
  center: 'center',
  end: 'flex-end',
  stretch: 'stretch',
};

const sheet = makeSheet(`
  :host { display: block; }

  .layout {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-start;
    align-items: flex-start;
    min-width: 0;
  }

  ${Object.entries(JUSTIFY).map(([value, css]) => `:host([horizontal-align="${value}"]) .layout { justify-content: ${css}; }`).join('\n  ')}
  ${Object.entries(ALIGN).map(([value, css]) => `:host([vertical-align="${value}"]) .layout { align-items: ${css}; }`).join('\n  ')}
`);

export class CaosLayout extends CaosElement {
  constructor() {
    super(sheet);
    const layout = document.createElement('div');
    layout.className = 'layout';
    layout.setAttribute('part', 'layout');
    layout.append(document.createElement('slot'));
    this.root.append(layout);
  }
}

define('caos-layout', CaosLayout);

declare global {
  interface HTMLElementTagNameMap {
    'caos-layout': CaosLayout;
  }
}
